import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Loader2 } from 'lucide-react';

export function DeleteModuleDialog({ pendingDelete, deleting, onCancel, onConfirm }) {
  return (
    <AlertDialog open={!!pendingDelete} onOpenChange={(open) => !open && !deleting && onCancel()}>
      <AlertDialogContent data-testid="delete-module-confirm">
        <AlertDialogHeader>
          <AlertDialogTitle>Delete this module?</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-2">
              <p>You're about to delete <span className="font-semibold text-foreground">"{pendingDelete?.title}"</span> and everything attached to it. This cannot be undone.</p>
              <ul className="list-disc list-inside text-sm space-y-1">
                <li>{pendingDelete?.total_sections || 0} section(s) and {pendingDelete?.total_slides || 0} slide(s)</li>
                <li>{pendingDelete?.total_quizzes || 0} quiz/quizzes and all questions</li>
                <li>{pendingDelete?.enrolled_count || 0} enrollment(s) and learner progress</li>
                <li>Quiz attempts and certificates issued for this module</li>
              </ul>
              {(pendingDelete?.total_quizzes || 0) > 0 && (
                <p className="text-sm px-3 py-2 rounded-md border border-amber-600/40 bg-amber-50 text-foreground"
                   data-testid="delete-quiz-warning">
                  This module has a Day-End Quiz. If you're replacing the presentation with an
                  updated version, <span className="font-semibold">export the quiz as CSV from the quiz
                  editor first</span> — you can import it into the new module's quiz in one step.
                </p>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={deleting} data-testid="delete-module-cancel">Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            disabled={deleting}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            data-testid="delete-module-confirm-btn"
          >
            {deleting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Deleting…</>) : 'Delete permanently'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function BulkDeleteDialog({
  open, onOpenChange, deleting, selectedCount, selectedModules, bulkImpact, onConfirm,
}) {
  return (
    <AlertDialog open={open} onOpenChange={(o) => !deleting && onOpenChange(o)}>
      <AlertDialogContent data-testid="bulk-delete-confirm">
        <AlertDialogHeader>
          <AlertDialogTitle>
            Delete {selectedCount} module{selectedCount === 1 ? '' : 's'}?
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3">
              <p>This will permanently delete the selected modules and everything attached to them. This cannot be undone.</p>
              <div className="rounded border p-3 bg-muted/40 text-sm space-y-1">
                <div><span className="font-semibold">{selectedCount}</span> module{selectedCount === 1 ? '' : 's'}</div>
                <div><span className="font-semibold">{bulkImpact.sections}</span> section{bulkImpact.sections === 1 ? '' : 's'} and <span className="font-semibold">{bulkImpact.slides}</span> slide{bulkImpact.slides === 1 ? '' : 's'}</div>
                <div><span className="font-semibold">{bulkImpact.quizzes}</span> quiz/quizzes and all their questions</div>
                <div><span className="font-semibold">{bulkImpact.enrollments}</span> enrollment{bulkImpact.enrollments === 1 ? '' : 's'}, plus all progress, attempts and certificates</div>
              </div>
              {selectedCount <= 8 && (
                <ul className="list-disc list-inside text-sm space-y-0.5 max-h-40 overflow-y-auto">
                  {selectedModules.map((m) => <li key={m.module_id}>"{m.title}"</li>)}
                </ul>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={deleting} data-testid="bulk-delete-cancel">Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            disabled={deleting}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            data-testid="bulk-delete-confirm-btn"
          >
            {deleting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Deleting…</>) : `Delete ${selectedCount} permanently`}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
